/**
 * Deterministic simulation core. `Sim.step(inputWord)` is the ONLY way state
 * advances, and its result depends solely on (current state, inputWord).
 * No wall-clock, no Math.random, no rendering concerns, no transcendental
 * functions (cross-engine determinism, see docs/ARCHITECTURE.md §2).
 *
 * Player forms are small controllers over a shared pipeline:
 * input edges → zones (portals/pads/orbs/teleports/checkpoints) → form update
 * → horizontal advance → vertical integrate/resolve (solids, one-ways,
 * slopes, ground) → hazards/bounds/completion.
 *
 * Dual mode simulates a second vertical body (`state.dual`) sharing x/form/
 * speed with the primary, receiving the same input (its gravity typically
 * inverted). Platformer mode replaces auto-scroll with left/right control,
 * turns frontal/ceiling deaths into stops, and completes on endgate contact.
 */
import { TICK_DT } from './core/tick';
import { Rng } from './core/rng';
import { StateHasher } from './core/hash';
import {
  RUNNER,
  INPUT_BUFFER_S,
  FORM_PHYSICS,
  type FormId,
  SPEED,
  VAULTER_HOLD_S,
  COYOTE_S,
  MINI_SCALE,
  MINI_IMPULSE,
  STREAK_SLOPE,
  STREAK_SLOPE_MINI,
  PAD_IMPULSE,
  ORB_IMPULSE,
  KILL_Y_MIN,
  KILL_Y_MAX,
  SLOPE_LAND_TOL,
} from './physics/constants';
import {
  type RuntimeLevel,
  type Collider,
  type SlopeCollider,
  type Zone,
  surfaceYAt,
} from './level/runtime';
import { aabbOverlap, type Aabb } from './collision/grid';
import {
  initialTriggerState,
  cloneTriggerState,
  groupState,
  ease,
  lerpHex,
  TRIGGER_BUDGET_PER_TICK,
  type TriggerRuntimeState,
  type TriggerDef,
  type ActiveTween,
  type AreaEffect,
  type EnterFx,
} from './triggers/triggers';

/** Input word bits (per tick). */
export const INPUT_PRIMARY = 1 << 0;
export const INPUT_LEFT = 1 << 1;
export const INPUT_RIGHT = 1 << 2;

export type PlayerStatus = 'alive' | 'dead' | 'won';

/** Per-body vertical state. The primary body's fields live flat on SimState;
 * the dual body (when active) is a second instance of this shape. */
export interface Body {
  y: number;
  vy: number;
  /** 1 = gravity pulls −y (normal); −1 = inverted. "Up" for jumps = this sign. */
  gravityDir: 1 | -1;
  grounded: boolean;
  /** Ticks since last grounded (coyote window); 0 while grounded. */
  airTicks: number;
  /** Remaining vaulter thrust ticks while holding. */
  thrustTicks: number;
  /** Render-facing spin/pitch, still simulated deterministically. */
  rotation: number;
}

export interface SimState extends Body {
  tick: number;
  x: number;
  form: FormId;
  /** Current horizontal speed magnitude, u/s. */
  speed: number;
  mini: boolean;
  mirror: boolean;
  /** Platformer facing (render + movement direction of last input). */
  facing: 1 | -1;
  /** Dash-orb state: while true and held, travel in a straight line. */
  dashing: boolean;
  /** Last platformer checkpoint object touched (−1 = none). */
  lastCheckpointId: number;
  status: PlayerStatus;
  jumps: number;
  bufferedPressTick: number;
  prevInput: number;
  /** One-shot zones (pads/orbs/teleports) consumed this attempt, in touch order. */
  usedZones: number[];
  /** Second body in dual mode; null otherwise. */
  dual: Body | null;
  /** Shard/mark collectible object ids gathered this attempt (kept only if
   * the attempt completes — the session persists them). */
  collected: number[];
  /** Trigger system state (tweens, groups, channels, camera, timewarp). */
  trig: TriggerRuntimeState;
  /** Furthest x reached (positional triggers fire on first crossing). */
  maxX: number;
}

const BUFFER_TICKS = Math.round(INPUT_BUFFER_S / TICK_DT);
const COYOTE_TICKS = Math.round(COYOTE_S / TICK_DT);
const VAULTER_TICKS = Math.round(VAULTER_HOLD_S / TICK_DT);
const GROUND_Y = 0;
/** Runner/vaulter airborne spin: 180° over one full runner jump arc. */
const AIR_SPIN = Math.PI / (2 * (RUNNER.jumpVelocity / RUNNER.gravity));

export class Sim {
  readonly level: RuntimeLevel;
  readonly rng: Rng;
  state: SimState;
  private candidates: Collider[] = [];
  private zoneCandidates: Zone[] = [];
  private slopeCandidates: SlopeCollider[] = [];

  /** When true (editor playtest), spawn at the level's startpos object. */
  private useStartPos: boolean;

  /** Sim-owned copies of group-movable colliders (positions follow group state). */
  private simGrouped: Array<{ base: Collider; groups: number[]; cur: Collider; active: boolean }>;

  constructor(level: RuntimeLevel, seed = 1, opts: { useStartPos?: boolean } = {}) {
    this.level = level;
    this.rng = new Rng(seed);
    this.useStartPos = opts.useStartPos ?? false;
    this.simGrouped = level.groupedColliders.map((g) => ({
      base: g.base,
      groups: g.groups,
      cur: { ...g.base },
      active: true,
    }));
    this.state = this.initialState();
    this.updateGroupedColliders();
  }

  initialState(): SimState {
    const sp = this.useStartPos ? this.level.startPos : null;
    const form = (sp?.form as FormId | undefined) ?? this.level.data.settings.startForm ?? 'runner';
    const speedBand =
      (sp?.speed as keyof typeof SPEED | undefined) ?? this.level.data.settings.speed;
    return {
      tick: 0,
      x: sp?.x ?? 0,
      y: sp ? sp.y : GROUND_Y + RUNNER.bodySize / 2,
      vy: 0,
      form,
      gravityDir: 1,
      speed: SPEED[speedBand] ?? SPEED[this.level.data.settings.speed],
      mini: false,
      mirror: false,
      facing: 1,
      dashing: false,
      lastCheckpointId: -1,
      grounded: true,
      airTicks: 0,
      thrustTicks: 0,
      status: 'alive',
      rotation: 0,
      jumps: 0,
      bufferedPressTick: -1,
      prevInput: 0,
      usedZones: [],
      dual: null,
      collected: [],
      trig: this.initialTrigState(),
      maxX: sp?.x ?? 0,
    };
  }

  private initialTrigState(): TriggerRuntimeState {
    const t = initialTriggerState();
    for (const ch of this.level.data.colors ?? []) t.channels[ch.id] = ch.hex;
    return t;
  }

  reset(): void {
    this.state = this.initialState();
    this.updateGroupedColliders();
  }

  /** Deep-copy snapshot (practice/platformer checkpoints, solver backtracking). */
  snapshot(): SimState {
    return {
      ...this.state,
      usedZones: [...this.state.usedZones],
      dual: this.state.dual ? { ...this.state.dual } : null,
      collected: [...this.state.collected],
      trig: cloneTriggerState(this.state.trig),
    };
  }

  restore(s: SimState): void {
    this.state = {
      ...s,
      usedZones: [...s.usedZones],
      dual: s.dual ? { ...s.dual } : null,
      collected: [...s.collected],
      trig: cloneTriggerState(s.trig),
    };
    this.updateGroupedColliders();
  }

  progress(): number {
    return Math.max(0, Math.min(1, this.state.x / this.level.endX));
  }

  get isPlatformer(): boolean {
    return this.level.mode === 'platformer';
  }

  private halfBody(): number {
    return (RUNNER.bodySize / 2) * (this.state.mini ? MINI_SCALE : 1);
  }

  private halfInner(): number {
    return (RUNNER.innerSize / 2) * (this.state.mini ? MINI_SCALE : 1);
  }

  private boxFor(body: Body, out: Aabb, half: number): Aabb {
    out.minX = this.state.x - half;
    out.maxX = this.state.x + half;
    out.minY = body.y - half;
    out.maxY = body.y + half;
    return out;
  }

  private static scratchA: Aabb = { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  private static scratchB: Aabb = { minX: 0, minY: 0, maxX: 0, maxY: 0 };

  private impulseScale(): number {
    return this.state.mini ? MINI_IMPULSE : 1;
  }

  // ==========================================================================

  step(input: number): void {
    const s = this.state;
    if (s.status !== 'alive') {
      s.prevInput = input;
      return;
    }
    s.tick++;

    // --- Triggers (move the world before the player acts) -------------------
    this.processTriggers();

    // --- Input edges & buffering -------------------------------------------
    const pressed = (input & INPUT_PRIMARY) !== 0 && (s.prevInput & INPUT_PRIMARY) === 0;
    const held = (input & INPUT_PRIMARY) !== 0;
    if (pressed) s.bufferedPressTick = s.tick;
    const buffered = s.bufferedPressTick >= 0 && s.tick - s.bufferedPressTick <= BUFFER_TICKS;

    // --- Horizontal velocity ------------------------------------------------
    let vx: number;
    if (this.isPlatformer) {
      const left = (input & INPUT_LEFT) !== 0;
      const right = (input & INPUT_RIGHT) !== 0;
      vx = (right ? s.speed : 0) - (left ? s.speed : 0);
      if (vx > 0) s.facing = 1;
      else if (vx < 0) s.facing = -1;
    } else {
      vx = s.speed;
    }

    // --- Interactive zones --------------------------------------------------
    let pressConsumed = this.processZones(s, true, pressed, buffered);
    if (s.status !== 'alive') return; // endgate zone can complete instantly
    if (s.dual) {
      pressConsumed =
        this.processZones(s.dual, false, pressed && !pressConsumed, buffered && !pressConsumed) ||
        pressConsumed;
    }

    // --- Form controllers ---------------------------------------------------
    if (!pressConsumed) pressConsumed = this.updateForm(s, pressed, held, buffered);
    if (s.dual)
      this.updateForm(s.dual, pressed && !pressConsumed, held, buffered && !pressConsumed);

    // --- Horizontal advance & frontal resolution ----------------------------
    s.x += vx * TICK_DT;
    if (!this.frontalPass(s, vx)) return;
    if (s.dual && !this.frontalPass(s.dual, vx)) return;

    // --- Vertical passes ----------------------------------------------------
    if (!this.verticalPass(s, held)) return;
    if (s.dual && !this.verticalPass(s.dual, held)) return;

    // --- Hazards & bounds ---------------------------------------------------
    if (!this.hazardPass(s)) return;
    if (s.dual && !this.hazardPass(s.dual)) return;

    // --- Completion ---------------------------------------------------------
    if (!this.isPlatformer && s.x >= this.level.endX) s.status = 'won';

    s.prevInput = input;
  }

  // --- Zones -----------------------------------------------------------------

  /** Returns true if a press was consumed (by an orb). */
  private processZones(
    body: Body,
    isPrimary: boolean,
    pressed: boolean,
    buffered: boolean,
  ): boolean {
    const s = this.state;
    const box = this.boxFor(body, Sim.scratchB, this.halfBody());
    const zones = this.level.zoneGrid.query(box, this.zoneCandidates);
    let consumed = false;
    zones.sort((a, b) => a.objectId - b.objectId); // deterministic order
    for (const z of zones) {
      if (!aabbOverlap(box, z)) continue;
      switch (z.type) {
        case 'portal':
          this.applyPortal(body, isPrimary, z);
          if (s.status !== 'alive') return consumed;
          break;
        case 'pad':
          if (!s.usedZones.includes(z.objectId)) {
            this.applyPad(body, z.kind);
            s.usedZones.push(z.objectId);
          }
          break;
        case 'orb':
          if (!consumed && (pressed || buffered) && !s.usedZones.includes(z.objectId)) {
            this.applyOrb(body, z.kind);
            s.usedZones.push(z.objectId);
            s.bufferedPressTick = -1;
            consumed = true;
          }
          break;
        case 'checkpoint':
          s.lastCheckpointId = z.objectId;
          break;
        case 'collectible':
          if (!s.usedZones.includes(z.objectId)) {
            s.usedZones.push(z.objectId);
            if (z.kind === 'key') {
              const id = z.itemId ?? 1;
              s.trig.items[id] = (s.trig.items[id] ?? 0) + 1;
            } else {
              s.collected.push(z.objectId); // shard/mark: persisted on completion
            }
          }
          break;
        case 'endgate':
          if (this.isPlatformer) s.status = 'won';
          break;
      }
    }
    return consumed;
  }

  private applyPortal(body: Body, isPrimary: boolean, z: Zone): void {
    const s = this.state;
    const kind = z.kind;
    if (kind.startsWith('form:')) {
      if (!isPrimary) return; // global effects apply once, via the primary
      const form = kind.slice(5) as FormId;
      if (FORM_PHYSICS[form] && s.form !== form) {
        s.form = form;
        s.thrustTicks = 0;
        s.grounded = false;
        s.rotation = 0;
        if (s.dual) {
          s.dual.thrustTicks = 0;
          s.dual.grounded = false;
          s.dual.rotation = 0;
        }
      }
    } else if (kind === 'gravity:up') {
      if (body.gravityDir !== -1) {
        body.gravityDir = -1;
        body.grounded = false;
      }
    } else if (kind === 'gravity:down') {
      if (body.gravityDir !== 1) {
        body.gravityDir = 1;
        body.grounded = false;
      }
    } else if (kind.startsWith('speed:')) {
      if (!isPrimary) return;
      const band = kind.slice(6) as keyof typeof SPEED;
      if (SPEED[band]) s.speed = SPEED[band];
    } else if (kind === 'size:mini') {
      if (isPrimary) s.mini = true;
    } else if (kind === 'size:normal') {
      if (isPrimary) s.mini = false;
    } else if (kind === 'mirror:on') {
      if (isPrimary) s.mirror = true;
    } else if (kind === 'mirror:off') {
      if (isPrimary) s.mirror = false;
    } else if (kind === 'dual:on') {
      if (isPrimary && !s.dual) {
        s.dual = {
          y: body.y,
          vy: -body.vy,
          gravityDir: body.gravityDir === 1 ? -1 : 1,
          grounded: false,
          airTicks: 0,
          thrustTicks: 0,
          rotation: 0,
        };
      }
    } else if (kind === 'dual:off') {
      if (isPrimary) s.dual = null;
    } else if (kind === 'teleport') {
      if (!s.usedZones.includes(z.objectId)) {
        s.usedZones.push(z.objectId);
        if (isPrimary) s.x += z.dx ?? 0;
        body.y += z.dy ?? 0;
        body.grounded = false;
      }
    }
  }

  private applyPad(body: Body, kind: string): void {
    const impulse = PAD_IMPULSE[kind];
    if (impulse === undefined) return;
    if (kind === 'flip') body.gravityDir = body.gravityDir === 1 ? -1 : 1;
    body.vy = impulse * this.impulseScale() * body.gravityDir;
    body.grounded = false;
    this.state.jumps++;
  }

  private applyOrb(body: Body, kind: string): void {
    const s = this.state;
    if (kind === 'blink') {
      this.blink(body);
      return;
    }
    if (kind === 'dash' || kind === 'gravdash') {
      if (kind === 'gravdash') body.gravityDir = body.gravityDir === 1 ? -1 : 1;
      s.dashing = true;
      body.vy = 0;
      body.grounded = false;
      s.jumps++;
      return;
    }
    const impulse = ORB_IMPULSE[kind];
    if (impulse === undefined) return;
    if (kind === 'flip' || kind === 'flip-boost') {
      body.gravityDir = body.gravityDir === 1 ? -1 : 1;
    }
    // Positive impulses push against gravity; the negative slam pushes along it.
    body.vy = impulse * this.impulseScale() * body.gravityDir;
    body.grounded = false;
    s.jumps++;
  }

  // --- Form controllers -------------------------------------------------------

  /** Returns true if the press was consumed by the form action. */
  private updateForm(body: Body, pressed: boolean, held: boolean, buffered: boolean): boolean {
    const s = this.state;
    const phys = FORM_PHYSICS[s.form];
    const up = body.gravityDir;
    switch (s.form) {
      case 'runner':
        if (body.grounded && (held || buffered)) {
          body.vy = phys.jumpVelocity * up * this.impulseScale();
          body.grounded = false;
          s.jumps++;
          s.bufferedPressTick = -1;
          return true;
        }
        return false;
      case 'vaulter':
        if (body.grounded && (pressed || buffered)) {
          body.vy = phys.jumpVelocity * up * this.impulseScale();
          body.thrustTicks = VAULTER_TICKS;
          body.grounded = false;
          s.jumps++;
          s.bufferedPressTick = -1;
          return true;
        }
        if (!held) body.thrustTicks = 0;
        return false;
      case 'hopper':
        if (pressed || (buffered && body.grounded)) {
          body.vy = phys.jumpVelocity * up * this.impulseScale();
          body.grounded = false;
          s.jumps++;
          s.bufferedPressTick = -1;
          return true;
        }
        return false;
      case 'roller':
        if ((pressed || buffered) && (body.grounded || body.airTicks <= COYOTE_TICKS)) {
          body.gravityDir = up === 1 ? -1 : 1;
          body.grounded = false;
          body.vy = 0;
          s.jumps++;
          s.bufferedPressTick = -1;
          return true;
        }
        return false;
      case 'blinker':
        if (pressed || buffered) {
          this.blink(body);
          s.bufferedPressTick = -1;
          return true;
        }
        return false;
      case 'pendul':
        if (pressed) {
          body.gravityDir = up === 1 ? -1 : 1;
          body.grounded = false;
          s.jumps++;
          s.bufferedPressTick = -1;
          return true;
        }
        return false;
      case 'glider':
      case 'streak':
        return false; // hold-driven; handled in the vertical pass
    }
  }

  /** Blinker/blink-orb: flip gravity, snap to the nearest surface that way. */
  private blink(body: Body): void {
    const s = this.state;
    const newDir = (body.gravityDir === 1 ? -1 : 1) as 1 | -1;
    body.gravityDir = newDir;
    const half = this.halfBody();
    const searchBox: Aabb = {
      minX: s.x - half,
      maxX: s.x + half,
      minY: KILL_Y_MIN,
      maxY: KILL_Y_MAX,
    };
    const near = this.collectColliders(searchBox);
    let target: number | null = null;
    for (const c of near) {
      if (c.kind !== 'solid') continue;
      if (c.minX >= s.x + half || c.maxX <= s.x - half) continue;
      if (newDir === -1) {
        if (c.minY >= body.y + half - 0.001) target = Math.min(target ?? Infinity, c.minY);
      } else {
        if (c.maxY <= body.y - half + 0.001) target = Math.max(target ?? -Infinity, c.maxY);
      }
    }
    if (newDir === 1 && target === null) target = GROUND_Y;
    if (target !== null) {
      body.y = newDir === 1 ? target + half : target - half;
      body.vy = 0;
      body.grounded = true;
      body.airTicks = 0;
      s.jumps++;
    } else {
      // No surface: launch toward the new "floor" (up when inverted) and let
      // the out-of-bounds check resolve it.
      body.vy = -900 * newDir;
      body.grounded = false;
    }
  }

  // --- Movement passes --------------------------------------------------------

  /** Frontal solids after x-advance. Returns false if the player died. */
  private frontalPass(body: Body, vx: number): boolean {
    const s = this.state;
    const phys = FORM_PHYSICS[s.form];
    const half = this.halfBody();
    const box = this.boxFor(body, Sim.scratchA, half);
    const near = this.collectColliders({
      minX: box.minX - 8,
      minY: box.minY - 8,
      maxX: box.maxX + 8,
      maxY: box.maxY + 8,
    });
    const up = body.gravityDir;
    // While climbing a slope, a solid whose top meets the ramp is walkable:
    // measure the step from the ramp surface at our leading edge, not the feet.
    let rampSurface = -Infinity;
    if (up === 1) {
      const slopes = this.level.slopeGrid.query(box, this.slopeCandidates);
      for (const sl of slopes) {
        if (s.x >= sl.minX && s.x <= sl.maxX) {
          rampSurface = Math.max(rampSurface, surfaceYAt(sl, box.maxX));
        }
      }
    }
    for (const c of near) {
      if (c.kind !== 'solid' || !aabbOverlap(box, c)) continue;
      if (phys.solidKills) {
        this.kill();
        return false;
      }
      const feet = up === 1 ? box.minY : box.maxY;
      const floorFace = up === 1 ? c.maxY : c.minY;
      const shallow = up === 1 ? floorFace - feet : feet - floorFace;
      if (shallow <= RUNNER.stepTolerance && shallow > -0.001 && body.vy * up <= 0) continue;
      if (up === 1 && c.maxY - rampSurface <= RUNNER.stepTolerance && body.vy * up <= 0) continue;
      if (up === 1 && (box.minY >= c.maxY - 0.001 || box.maxY <= c.minY + 0.001)) continue;
      if (up === -1 && (box.maxY <= c.minY + 0.001 || box.minY >= c.maxY - 0.001)) continue;
      if (this.isPlatformer) {
        // Walls stop instead of kill. Clamp x against the face we ran into.
        if (vx > 0) s.x = c.minX - half;
        else if (vx < 0) s.x = c.maxX + half;
        this.boxFor(body, box, half);
        continue;
      }
      this.kill();
      return false;
    }
    return true;
  }

  /** Gravity integration + vertical resolution. Returns false if died. */
  private verticalPass(body: Body, held: boolean): boolean {
    const s = this.state;
    const phys = FORM_PHYSICS[s.form];
    const up = body.gravityDir;
    const half = this.halfBody();

    if (s.dashing) {
      if (held) {
        body.vy = 0; // straight-line dash suspends gravity
      } else {
        s.dashing = false;
      }
    }
    if (s.form === 'streak') {
      const slope = s.mini ? STREAK_SLOPE_MINI : STREAK_SLOPE;
      if (!s.dashing) body.vy = (held ? 1 : -1) * slope * s.speed * up;
    } else if (!s.dashing && (!body.grounded || s.form === 'glider' || s.form === 'pendul')) {
      let a = s.form === 'glider' && held ? phys.holdAccel * up : -phys.gravity * up;
      if (s.form === 'vaulter' && body.thrustTicks > 0 && held) {
        a = phys.holdAccel * up;
        body.thrustTicks--;
      }
      body.vy += a * TICK_DT;
      const t = phys.terminal;
      if (t > 0) {
        if (body.vy > t) body.vy = t;
        if (body.vy < -t) body.vy = -t;
      }
    }

    const prevLow = body.y - half;
    const prevHigh = body.y + half;
    body.y += body.vy * TICK_DT;
    const box = this.boxFor(body, Sim.scratchA, half);
    const near = this.collectColliders({
      minX: box.minX - 8,
      minY: box.minY - 8,
      maxX: box.maxX + 8,
      maxY: box.maxY + 8,
    });

    let landedAt: number | null = null;
    for (const c of near) {
      if (c.kind === 'hazard' || !aabbOverlap(box, c)) continue;
      if (c.kind === 'oneway') {
        // Only the face opposing the fall interacts, and only from clear above.
        if (up === 1 && body.vy <= 0 && prevLow >= c.maxY - 0.001) {
          landedAt = Math.max(landedAt ?? -Infinity, c.maxY);
        } else if (up === -1 && body.vy >= 0 && prevHigh <= c.minY + 0.001) {
          landedAt = Math.min(landedAt ?? Infinity, c.minY);
        }
        continue;
      }
      if (phys.solidKills) {
        this.kill();
        return false;
      }
      if (up === 1) {
        if (body.vy <= 0 && prevLow >= c.maxY - 0.001) {
          landedAt = Math.max(landedAt ?? -Infinity, c.maxY);
        } else if (body.vy > 0 && prevHigh <= c.minY + 0.001) {
          if (phys.ceilingKills && !this.isPlatformer) {
            this.kill();
            return false;
          }
          body.y = c.minY - half;
          body.vy = 0;
          this.boxFor(body, box, half);
        } else if (c.maxY - (body.y - half) <= RUNNER.stepTolerance && body.vy <= 0) {
          landedAt = Math.max(landedAt ?? -Infinity, c.maxY);
        }
      } else {
        if (body.vy >= 0 && prevHigh <= c.minY + 0.001) {
          landedAt = Math.min(landedAt ?? Infinity, c.minY);
        } else if (body.vy < 0 && prevLow >= c.maxY - 0.001) {
          if (phys.ceilingKills && !this.isPlatformer) {
            this.kill();
            return false;
          }
          body.y = c.maxY + half;
          body.vy = 0;
          this.boxFor(body, box, half);
        } else if (body.y + half - c.minY <= RUNNER.stepTolerance && body.vy >= 0) {
          landedAt = Math.min(landedAt ?? Infinity, c.minY);
        }
      }
    }

    // Slopes (normal-gravity walkable ramps; streak still dies on them).
    if (up === 1) {
      const slopes = this.level.slopeGrid.query(box, this.slopeCandidates);
      for (const sl of slopes) {
        if (s.x < sl.minX || s.x > sl.maxX) continue;
        const surface = surfaceYAt(sl, s.x);
        const feet = body.y - half;
        if (phys.solidKills && feet < surface - 0.001) {
          this.kill();
          return false;
        }
        const withinSnap = feet <= surface + SLOPE_LAND_TOL && feet >= surface - 14;
        if (body.vy <= 0 && withinSnap) {
          landedAt = Math.max(landedAt ?? -Infinity, surface);
        } else if (body.grounded && withinSnap) {
          landedAt = Math.max(landedAt ?? -Infinity, surface);
        }
      }
    }

    // Permanent ground plane (bottom floor; streak slides along it).
    if (up === 1 && body.y - half <= GROUND_Y && body.vy <= 0) {
      landedAt = Math.max(landedAt ?? -Infinity, GROUND_Y);
    }
    if (s.form === 'streak' && body.y - half < GROUND_Y) {
      body.y = GROUND_Y + half;
    }

    if (landedAt !== null) {
      body.y = up === 1 ? landedAt + half : landedAt - half;
      body.vy = 0;
      if (!body.grounded) {
        body.grounded = true;
        if (s.form === 'runner' || s.form === 'vaulter') {
          body.rotation = Math.round(body.rotation / (Math.PI / 2)) * (Math.PI / 2);
        }
      }
      body.airTicks = 0;
    } else {
      if (body.grounded && !this.hasSupport(body, near, half, up)) body.grounded = false;
      if (!body.grounded) body.airTicks++;
    }

    this.updateRotation(body);
    return true;
  }

  private hazardPass(body: Body): boolean {
    const inner = this.boxFor(body, Sim.scratchB, this.halfInner());
    const near = this.collectColliders(inner);
    for (const c of near) {
      if (c.kind === 'hazard' && aabbOverlap(inner, c)) {
        this.kill();
        return false;
      }
    }
    if (body.y < KILL_Y_MIN || body.y > KILL_Y_MAX) {
      this.kill();
      return false;
    }
    return true;
  }

  private updateRotation(body: Body): void {
    const s = this.state;
    switch (s.form) {
      case 'runner':
      case 'vaulter':
        if (!body.grounded) body.rotation += AIR_SPIN * TICK_DT * body.gravityDir;
        break;
      case 'roller':
        body.rotation += (s.speed / (this.halfBody() * 2)) * TICK_DT;
        break;
      case 'glider':
      case 'pendul':
      case 'hopper': {
        const target = Math.max(-0.6, Math.min(0.6, body.vy / 700));
        body.rotation += (target - body.rotation) * 0.15;
        break;
      }
      case 'streak':
        body.rotation = body.vy > 0 ? 0.6 : -0.6;
        break;
      case 'blinker':
        body.rotation = 0;
        break;
    }
  }

  private hasSupport(body: Body, near: readonly Collider[], half: number, up: 1 | -1): boolean {
    const s = this.state;
    if (up === 1) {
      const feet = body.y - half;
      if (feet <= GROUND_Y + 0.001) return true;
      for (const c of near) {
        if (c.kind === 'hazard') continue;
        if (Math.abs(feet - c.maxY) <= 0.5 && s.x + half > c.minX && s.x - half < c.maxX)
          return true;
      }
      const slopes = this.level.slopeGrid.query(
        { minX: s.x - half, maxX: s.x + half, minY: feet - 20, maxY: feet + 20 },
        this.slopeCandidates,
      );
      for (const sl of slopes) {
        if (s.x >= sl.minX && s.x <= sl.maxX && Math.abs(feet - surfaceYAt(sl, s.x)) <= 4)
          return true;
      }
      return false;
    }
    const head = body.y + half;
    for (const c of near) {
      if (c.kind !== 'solid') continue;
      if (Math.abs(head - c.minY) <= 0.5 && s.x + half > c.minX && s.x - half < c.maxX) return true;
    }
    return false;
  }

  // --- Trigger system ---------------------------------------------------------

  private processTriggers(): void {
    const s = this.state;
    const t = s.trig;
    let budget = TRIGGER_BUDGET_PER_TICK;

    // Positional activations: fire once when the player first crosses x.
    if (s.x > s.maxX) s.maxX = s.x;
    for (const trig of this.level.triggers) {
      if (trig.x > s.maxX) break; // sorted by x
      if (trig.spawnOnly || t.fired.includes(trig.id)) continue;
      if (budget-- <= 0) {
        t.overflow = true;
        break;
      }
      this.activateTrigger(trig);
    }

    // Scheduled spawn firings.
    if (t.pending.length > 0) {
      const due = t.pending.filter((p) => p.tick <= s.tick);
      if (due.length > 0) {
        t.pending = t.pending.filter((p) => p.tick > s.tick);
        for (const p of due) {
          for (const trig of this.level.triggers) {
            if (!trig.groups.includes(p.group)) continue;
            if (!trig.spawnOnly && t.fired.includes(trig.id)) continue;
            if (budget-- <= 0) {
              t.overflow = true;
              break;
            }
            this.activateTrigger(trig);
          }
        }
      }
    }

    this.advanceTweens();
    this.updateGroupedColliders(true);
  }

  private activateTrigger(trig: TriggerDef): void {
    const s = this.state;
    const t = s.trig;
    if (trig.maxActivations > 0 && (t.counts[trig.id] ?? 0) >= trig.maxActivations) return;
    t.counts[trig.id] = (t.counts[trig.id] ?? 0) + 1;
    if (!trig.spawnOnly) t.fired.push(trig.id);
    const start = s.tick;
    const end = start + trig.durationTicks;
    const tween: ActiveTween = {
      kind: trig.kind,
      target: trig.target,
      startTick: start,
      endTick: end,
      easing: trig.easing,
      dx: trig.dx,
      dy: trig.dy,
      degrees: trig.degrees,
      fromAlpha: 1,
      toAlpha: trig.alpha,
      fromZoom: t.camera.zoom,
      toZoom: trig.zoom,
      fromHex: '#ffffff',
      toHex: trig.hex,
      strength: trig.strength,
      fromScale: 1,
      toScale: trig.scale,
      appliedX: 0,
      appliedY: 0,
      appliedDeg: 0,
      aux: s.y,
      pausedAt: -1,
    };
    switch (trig.kind) {
      case 'toggle': {
        groupState(t, trig.target).visible = trig.visible;
        return;
      }
      case 'spawn': {
        t.pending.push({ tick: s.tick + Math.max(1, trig.delayTicks), group: trig.target });
        return;
      }
      case 'stop': {
        // Freeze the group's running tweens where they are.
        t.tweens = t.tweens.filter((tw) => tw.target !== trig.target);
        return;
      }
      case 'pause': {
        for (const tw of t.tweens) {
          if (tw.target === trig.target && tw.pausedAt < 0) tw.pausedAt = s.tick;
        }
        return;
      }
      case 'resume': {
        for (const tw of t.tweens) {
          if (tw.target === trig.target && tw.pausedAt >= 0) {
            const shift = s.tick - tw.pausedAt;
            tw.startTick += shift;
            tw.endTick += shift;
            tw.pausedAt = -1;
          }
        }
        return;
      }
      case 'scale':
        tween.fromScale = groupState(t, trig.target).scale;
        break;
      case 'followy':
        tween.strength = trig.factor; // follow multiplier rides in `strength`
        break;
      case 'camera:rotate':
        tween.fromZoom = t.camera.rot; // reuse from/to slots for the roll angle
        tween.toZoom = trig.degrees;
        break;
      case 'camera:static': {
        t.camera.staticOn = true;
        t.camera.staticX = trig.dx; // dx/dy arrive pre-scaled to units
        t.camera.staticY = trig.dy;
        return;
      }
      case 'camera:free': {
        t.camera.staticOn = false;
        return;
      }
      case 'end': {
        s.status = 'won';
        return;
      }
      case 'pickup': {
        t.items[trig.target] = (t.items[trig.target] ?? 0) + (trig.factor || 1);
        return;
      }
      case 'item:spawn-if': {
        // Fires spawn group `value` (via dx slot? no — props.value) when the
        // item count reaches the threshold in `factor`.
        if ((t.items[trig.target] ?? 0) >= (trig.factor || 1)) {
          t.pending.push({ tick: s.tick + 1, group: Math.round(trig.value) });
        }
        return;
      }
      case 'camera:edge': {
        const edges = t.edges ?? { minX: null, maxX: null, minY: null, maxY: null };
        // dx carries the boundary position (already scaled to units).
        if (trig.side === 'left') edges.minX = trig.dx;
        else if (trig.side === 'right') edges.maxX = trig.dx;
        else if (trig.side === 'bottom') edges.minY = trig.dy;
        else edges.maxY = trig.dy;
        t.edges = edges;
        return;
      }
      case 'camera:edge-off': {
        t.edges = null;
        return;
      }
      case 'gradient': {
        t.gradient = {
          hexA: trig.hex,
          hexB: trig.hex2,
          alpha: Math.max(0, Math.min(1, trig.alpha)),
          vertical: trig.vertical,
        };
        return;
      }
      case 'gradient:off': {
        t.gradient = null;
        return;
      }
      case 'keyframe': {
        if (trig.path.length === 0) return;
        const total = trig.path.reduce((sum, seg) => sum + seg.ticks, 0);
        tween.endTick = trig.loop ? Number.MAX_SAFE_INTEGER : start + total;
        tween.path = trig.path;
        tween.loop = trig.loop;
        t.tweens.push(tween);
        return;
      }
      case 'timewarp': {
        t.timewarp = Math.max(0.25, Math.min(2, trig.factor));
        return;
      }
      case 'camera:shake': {
        t.camera.shakeAmp = trig.strength;
        t.camera.shakeEndTick = s.tick + Math.max(1, trig.durationTicks);
        t.camera.shakeDur = Math.max(1, trig.durationTicks);
        return;
      }
      case 'alpha':
        tween.fromAlpha = groupState(t, trig.target).alpha;
        break;
      case 'color':
      case 'pulse':
        tween.fromHex = t.channels[trig.target] ?? '#ffffff';
        break;
      case 'camera:zoom':
        tween.fromZoom = t.camera.zoom;
        break;
      case 'move':
      case 'rotate':
      case 'camera:offset':
        break;
      default:
        if (trig.kind.startsWith('shader:')) {
          const fx = trig.kind.slice(7);
          // Shockwave/shockline are one-shot pulses; others tween current → strength.
          tween.fromAlpha = fx === 'shockwave' || fx === 'shockline' ? 0 : (t.shaderFx[fx] ?? 0);
          tween.toAlpha = trig.strength;
        } else if (trig.kind.startsWith('area:')) {
          // Re-triggering replaces the same trigger's field; radius 0 removes.
          t.areas = t.areas.filter((a) => a.triggerId !== trig.id);
          if (trig.radius > 0) {
            t.areas.push({
              triggerId: trig.id,
              kind: trig.kind.slice(5) as AreaEffect['kind'],
              x: trig.x,
              y: trig.y,
              radius: trig.radius,
              dx: trig.dx,
              dy: trig.dy,
              degrees: trig.degrees,
              scale: trig.scale,
              alpha: trig.alpha,
              hex: trig.hex,
            });
          }
          return;
        } else if (trig.kind.startsWith('enter:')) {
          const kind = trig.kind.slice(6);
          if (kind === 'off') t.enter = null;
          else {
            t.enter = {
              kind: kind as EnterFx['kind'],
              dx: trig.dx,
              dy: trig.dy,
              degrees: trig.degrees,
              scale: trig.scale,
              width: trig.radius,
            };
          }
          return;
        }
        break;
    }
    t.tweens.push(tween);
  }

  private advanceTweens(): void {
    const s = this.state;
    const t = s.trig;
    if (t.tweens.length === 0 && t.camera.shakeAmp === 0) return;
    let anyDone = false;
    for (const tw of t.tweens) {
      if (tw.pausedAt >= 0) continue; // suspended by a pause trigger
      const dur = tw.endTick - tw.startTick;
      const raw = dur <= 0 ? 1 : (s.tick - tw.startTick) / dur;
      const f = ease(tw.easing, raw);
      switch (tw.kind) {
        case 'move': {
          const g = groupState(t, tw.target);
          g.dx += tw.dx * f - tw.appliedX;
          g.dy += tw.dy * f - tw.appliedY;
          tw.appliedX = tw.dx * f;
          tw.appliedY = tw.dy * f;
          break;
        }
        case 'rotate': {
          const g = groupState(t, tw.target);
          g.rot += tw.degrees * f - tw.appliedDeg;
          tw.appliedDeg = tw.degrees * f;
          break;
        }
        case 'camera:offset': {
          t.camera.dx += tw.dx * f - tw.appliedX;
          t.camera.dy += tw.dy * f - tw.appliedY;
          tw.appliedX = tw.dx * f;
          tw.appliedY = tw.dy * f;
          break;
        }
        case 'alpha':
          groupState(t, tw.target).alpha = tw.fromAlpha + (tw.toAlpha - tw.fromAlpha) * f;
          break;
        case 'scale':
          groupState(t, tw.target).scale = tw.fromScale + (tw.toScale - tw.fromScale) * f;
          break;
        case 'followy': {
          // Group mirrors the player's vertical movement, scaled by the factor.
          const g = groupState(t, tw.target);
          g.dy += (s.y - tw.aux) * tw.strength;
          tw.aux = s.y;
          break;
        }
        case 'keyframe': {
          const path = tw.path ?? [];
          const total = path.reduce((sum, seg) => sum + seg.ticks, 0);
          if (total <= 0) break;
          let elapsed = s.tick - tw.startTick;
          elapsed = tw.loop ? elapsed % total : Math.min(elapsed, total);
          let px = 0;
          let py = 0;
          let rem = elapsed;
          for (const seg of path) {
            if (rem >= seg.ticks) {
              px += seg.dx;
              py += seg.dy;
              rem -= seg.ticks;
            } else {
              const frac = rem / seg.ticks;
              px += seg.dx * frac;
              py += seg.dy * frac;
              break;
            }
          }
          const g = groupState(t, tw.target);
          g.dx += px - tw.appliedX;
          g.dy += py - tw.appliedY;
          tw.appliedX = px;
          tw.appliedY = py;
          break;
        }
        case 'camera:zoom':
          t.camera.zoom = tw.fromZoom + (tw.toZoom - tw.fromZoom) * f;
          break;
        case 'camera:rotate':
          t.camera.rot = tw.fromZoom + (tw.toZoom - tw.fromZoom) * f;
          break;
        case 'color':
          t.channels[tw.target] = lerpHex(tw.fromHex, tw.toHex, f);
          break;
        case 'pulse': {
          // Triangle: out to the pulse color and back.
          const p = Math.max(0, Math.min(1, raw));
          t.channels[tw.target] = lerpHex(tw.fromHex, tw.toHex, 1 - Math.abs(2 * p - 1));
          break;
        }
        default:
          if (tw.kind.startsWith('shader:')) {
            const fx = tw.kind.slice(7);
            if (fx === 'shockwave' || fx === 'shockline') {
              // One-shot wave: progress 0→1 with amplitude alongside.
              t.shaderFx[fx] = Math.max(0, Math.min(1, raw));
              t.shaderFx[`${fx}Amp`] = tw.toAlpha;
            } else {
              t.shaderFx[fx] = tw.fromAlpha + (tw.toAlpha - tw.fromAlpha) * f;
            }
          }
          break;
      }
      if (s.tick >= tw.endTick) {
        anyDone = true;
        if (tw.kind === 'pulse') t.channels[tw.target] = tw.fromHex;
        if (tw.kind === 'shader:shockwave' || tw.kind === 'shader:shockline') {
          const fx = tw.kind.slice(7);
          delete t.shaderFx[fx];
          delete t.shaderFx[`${fx}Amp`];
        } else if (tw.kind.startsWith('shader:') && tw.toAlpha === 0) {
          delete t.shaderFx[tw.kind.slice(7)]; // fully faded out: drop the key
        }
      }
    }
    if (anyDone) t.tweens = t.tweens.filter((tw) => tw.pausedAt >= 0 || s.tick < tw.endTick);
    if (t.camera.shakeAmp !== 0 && s.tick >= t.camera.shakeEndTick) t.camera.shakeAmp = 0;
  }

  /** Current shake amplitude (linear decay), for the renderer. */
  currentShake(): number {
    const c = this.state.trig.camera;
    if (c.shakeAmp === 0) return 0;
    const remaining = c.shakeEndTick - this.state.tick;
    if (remaining <= 0) return 0;
    return (c.shakeAmp * remaining) / c.shakeDur;
  }

  /**
   * Reposition grouped colliders from group state. When `carryRiders` is set
   * (the per-tick trigger pass), bodies standing on a moving collider are
   * carried by its delta — platform riding.
   */
  private updateGroupedColliders(carryRiders = false): void {
    const t = this.state.trig;
    const s = this.state;
    const half = this.halfBody();
    for (const gc of this.simGrouped) {
      let dx = 0;
      let dy = 0;
      let visible = true;
      for (const gid of gc.groups) {
        const g = t.groups[gid];
        if (!g) continue;
        dx += g.dx;
        dy += g.dy;
        if (!g.visible) visible = false;
      }
      const deltaX = gc.base.minX + dx - gc.cur.minX;
      const deltaY = gc.base.minY + dy - gc.cur.minY;
      if (carryRiders && gc.active && (deltaX !== 0 || deltaY !== 0)) {
        // Riding check against the collider's OLD position (where feet were).
        const carry = (body: Body): void => {
          if (!body.grounded) return;
          const overX = s.x + half > gc.cur.minX && s.x - half < gc.cur.maxX;
          if (!overX) return;
          if (body.gravityDir === 1 && Math.abs(body.y - half - gc.cur.maxY) <= 1.5) {
            s.x += deltaX;
            body.y += deltaY;
          } else if (body.gravityDir === -1 && Math.abs(body.y + half - gc.cur.minY) <= 1.5) {
            s.x += deltaX;
            body.y += deltaY;
          }
        };
        carry(s);
        if (s.dual) carry(s.dual);
      }
      gc.active = visible;
      gc.cur.minX = gc.base.minX + dx;
      gc.cur.maxX = gc.base.maxX + dx;
      gc.cur.minY = gc.base.minY + dy;
      gc.cur.maxY = gc.base.maxY + dy;
    }
  }

  /** Static-grid query plus the sim's grouped (movable) colliders and any
   * still-locked doors. */
  private collectColliders(box: Aabb): Collider[] {
    const out = this.level.grid.query(box, this.candidates);
    for (const gc of this.simGrouped) {
      if (!gc.active) continue;
      if (
        gc.cur.minX < box.maxX &&
        gc.cur.maxX > box.minX &&
        gc.cur.minY < box.maxY &&
        gc.cur.maxY > box.minY
      ) {
        out.push(gc.cur);
      }
    }
    for (const lock of this.level.locks) {
      if ((this.state.trig.items[lock.itemId] ?? 0) >= lock.needed) continue; // opened
      const c = lock.collider;
      if (c.minX < box.maxX && c.maxX > box.minX && c.minY < box.maxY && c.maxY > box.minY) {
        out.push(c);
      }
    }
    return out;
  }

  private kill(): void {
    this.state.status = 'dead';
  }

  /** Deterministic fingerprint of the full sim state (see TEST_PLAN). */
  hash(): number {
    const s = this.state;
    const h = new StateHasher()
      .addInt(s.tick)
      .addNumber(s.x)
      .addNumber(s.y)
      .addNumber(s.vy)
      .addInt(s.gravityDir)
      .addNumber(s.speed)
      .addInt(
        (s.mini ? 1 : 0) | (s.mirror ? 2 : 0) | (s.dashing ? 4 : 0) | (s.facing === 1 ? 8 : 0),
      )
      .addInt(s.grounded ? 1 : 0)
      .addInt(s.airTicks)
      .addInt(s.thrustTicks)
      .addInt(s.status === 'alive' ? 0 : s.status === 'dead' ? 1 : 2)
      .addNumber(s.rotation)
      .addInt(s.jumps)
      .addInt(s.bufferedPressTick)
      .addInt(s.prevInput)
      .addInt(s.lastCheckpointId)
      .addInt(this.rng.state);
    h.addInt(s.usedZones.length);
    for (const id of s.usedZones) h.addInt(id);
    h.addInt(FORM_ORDER.indexOf(s.form));
    // Trigger system state (sorted keys for deterministic iteration).
    const t = s.trig;
    h.addNumber(s.maxX)
      .addNumber(t.timewarp)
      .addInt(t.overflow ? 1 : 0);
    h.addInt(t.fired.length);
    for (const id of t.fired) h.addInt(id);
    h.addInt(t.pending.length);
    for (const p of t.pending) h.addInt(p.tick).addInt(p.group);
    h.addInt(t.tweens.length);
    for (const tw of t.tweens) {
      h.addInt(tw.startTick).addInt(tw.endTick).addInt(tw.target).addInt(tw.pausedAt);
      h.addNumber(tw.appliedX).addNumber(tw.appliedY).addNumber(tw.appliedDeg).addNumber(tw.aux);
    }
    const countIds = Object.keys(t.counts)
      .map(Number)
      .sort((a, b) => a - b);
    h.addInt(countIds.length);
    for (const cid of countIds) h.addInt(cid).addInt(t.counts[cid] ?? 0);
    const groupIds = Object.keys(t.groups)
      .map(Number)
      .sort((a, b) => a - b);
    h.addInt(groupIds.length);
    for (const gid of groupIds) {
      const g = t.groups[gid];
      if (!g) continue;
      h.addInt(gid).addNumber(g.dx).addNumber(g.dy).addNumber(g.rot).addNumber(g.alpha);
      h.addNumber(g.scale);
      h.addInt(g.visible ? 1 : 0);
    }
    const chIds = Object.keys(t.channels)
      .map(Number)
      .sort((a, b) => a - b);
    h.addInt(chIds.length);
    for (const cid of chIds) {
      h.addInt(cid);
      const hex = t.channels[cid] ?? '#000000';
      h.addInt(parseInt(hex.slice(1), 16));
    }
    h.addNumber(t.camera.zoom).addNumber(t.camera.dx).addNumber(t.camera.dy);
    h.addNumber(t.camera.rot).addInt(t.camera.staticOn ? 1 : 0);
    h.addNumber(t.camera.staticX).addNumber(t.camera.staticY);
    h.addNumber(t.camera.shakeAmp).addInt(t.camera.shakeEndTick);
    const fxKeys = Object.keys(t.shaderFx).sort();
    h.addInt(fxKeys.length);
    for (const k of fxKeys) {
      for (let i = 0; i < k.length; i++) h.addInt(k.charCodeAt(i));
      h.addNumber(t.shaderFx[k] ?? 0);
    }
    h.addInt(t.areas.length);
    for (const a of t.areas) {
      h.addInt(a.triggerId).addNumber(a.x).addNumber(a.radius);
      h.addNumber(a.dx).addNumber(a.dy).addNumber(a.degrees).addNumber(a.scale).addNumber(a.alpha);
    }
    h.addInt(t.enter ? 1 : 0);
    if (t.enter) {
      h.addNumber(t.enter.dx).addNumber(t.enter.dy).addNumber(t.enter.degrees);
      h.addNumber(t.enter.scale).addNumber(t.enter.width);
    }
    h.addInt(t.gradient ? 1 : 0);
    if (t.gradient) {
      h.addInt(parseInt(t.gradient.hexA.slice(1), 16));
      h.addInt(parseInt(t.gradient.hexB.slice(1), 16));
      h.addNumber(t.gradient.alpha).addInt(t.gradient.vertical ? 1 : 0);
    }
    h.addInt(t.edges ? 1 : 0);
    if (t.edges) {
      h.addNumber(t.edges.minX ?? -1e9).addNumber(t.edges.maxX ?? 1e9);
      h.addNumber(t.edges.minY ?? -1e9).addNumber(t.edges.maxY ?? 1e9);
    }
    const itemIds = Object.keys(t.items)
      .map(Number)
      .sort((a, b) => a - b);
    h.addInt(itemIds.length);
    for (const iid of itemIds) h.addInt(iid).addInt(t.items[iid] ?? 0);
    h.addInt(s.collected.length);
    for (const cid of s.collected) h.addInt(cid);
    if (s.dual) {
      h.addInt(1)
        .addNumber(s.dual.y)
        .addNumber(s.dual.vy)
        .addInt(s.dual.gravityDir)
        .addInt(s.dual.grounded ? 1 : 0)
        .addInt(s.dual.airTicks)
        .addInt(s.dual.thrustTicks)
        .addNumber(s.dual.rotation);
    } else {
      h.addInt(0);
    }
    return h.digest();
  }
}

const FORM_ORDER: FormId[] = [
  'runner',
  'glider',
  'roller',
  'hopper',
  'streak',
  'vaulter',
  'blinker',
  'pendul',
];
